/**
 * Apply/Registration Service
 * API functions for the apply page (public endpoints, no auth required)
 */

import apiClient, { type ApiError } from '@/lib/api/apiClient'
import type { ExtendedAxiosRequestConfig } from '@/lib/api/types'
import { extractErrorMessage } from '@/lib/utils/errorHandling'
import type { InviteDetails, InviteErrorResponse } from '../types/apply'

/**
 * The apiClient interceptor rejects with `{ message, status, data }`, while a raw
 * axios failure carries `response.data` — read the body from whichever is present,
 * otherwise `email` / `already used` classification is lost.
 */
function resolveInviteErrorBody(error: unknown): {
  body?: InviteErrorResponse | string
  status: number
} {
  if (error && typeof error === 'object') {
    const shaped = error as {
      response?: { data?: InviteErrorResponse | string; status?: number }
      data?: InviteErrorResponse | string
      status?: number
    }
    if (shaped.response?.data !== undefined) {
      return { body: shaped.response.data, status: shaped.response.status ?? 400 }
    }
    if (shaped.data !== undefined) {
      return { body: shaped.data, status: shaped.status ?? 400 }
    }
  }
  return { status: 400 }
}

/** Angular register.component branches on email / "already been used" / "already registered". */
function buildInviteError(error: unknown, fallbackMessage: string): ApiError {
  const { body, status } = resolveInviteErrorBody(error)

  if (body && typeof body === 'object') {
    // Expired code — Angular swaps the code in the URL and refetches.
    if (body.new_invite_code) {
      return {
        message: body.message || 'This invite code has expired.',
        status,
        newInviteCode: body.new_invite_code,
        type: 'INVITE_EXPIRED',
      } as ApiError & { newInviteCode: string; type: string }
    }
    if (body.email) {
      return {
        message: body.message || 'User is already registered with this email.',
        status,
        email: body.email,
        type: 'USER_ALREADY_REGISTERED',
      } as ApiError & { email: string; type: string }
    }

    const errorMessage = body.message || body.error || ''
    if (typeof errorMessage === 'string' && errorMessage.includes('already been used')) {
      return { message: errorMessage, status, type: 'INVITE_ALREADY_USED' } as ApiError & {
        type: string
      }
    }
    if (typeof errorMessage === 'string' && errorMessage.includes('already registered')) {
      return { message: errorMessage, status, type: 'USER_ALREADY_REGISTERED' } as ApiError & {
        type: string
      }
    }
    if (body.message) {
      return { message: body.message, status } as ApiError
    }
  }

  if (typeof body === 'string' && body.trim()) {
    const type = body.includes('already been used') ? 'INVITE_ALREADY_USED' : undefined
    return { message: body, status, ...(type ? { type } : {}) } as ApiError
  }

  return {
    message: extractErrorMessage(error, fallbackMessage),
    status: (error as ApiError).status || 400,
  } as ApiError
}

/**
 * Get invite details by invite code
 * Public endpoint - no authentication required
 * 
 * @param inviteId - The invite code
 * @returns Invite details for pre-filling registration form
 * @throws ApiError with specific error handling for:
 *   - User already registered (includes email in error)
 *   - Invite code already used
 *   - New invite code available (includes new_invite_code)
 */
export async function getInviteDetails(inviteId: string): Promise<InviteDetails> {
  try {
    const response = await apiClient.get<InviteDetails>(
      `/invite/${inviteId}`,
      {
        params: { page: 'register' },
        skipAuth: true,
      } as ExtendedAxiosRequestConfig
    )
    
    return response.data
  } catch (error: unknown) {
    throw buildInviteError(error, 'Failed to fetch invite details.')
  }
}

/**
 * Get co-applicant invite details by invite code
 * Public endpoint - no authentication required
 * 
 * @param inviteId - The co-applicant invite code (usually prefixed with CO-)
 * @returns Invite details for pre-filling registration form
 * @throws ApiError with specific error handling
 */
export async function getCoApplicantInviteDetails(inviteId: string): Promise<InviteDetails> {
  try {
    const response = await apiClient.get<InviteDetails>(
      `/coapplicant_invite/${inviteId}`,
      {
        params: { page: 'register' },
        skipAuth: true,
      } as ExtendedAxiosRequestConfig
    )
    
    return response.data
  } catch (error: unknown) {
    throw buildInviteError(error, 'Failed to fetch co-applicant invite details.')
  }
}

/**
 * Lookup invite by email/mobile/name
 * Used for auto-detecting existing invites during registration
 * 
 * @param params - Lookup parameters
 * @returns Array of matching invites or empty array
 */
export async function lookupInvite(params: {
  email?: string
  mobile_no?: string
  first_name?: string
  last_name?: string
  birthdate?: string
  security_number?: string
}): Promise<InviteDetails[]> {
  try {
    const queryParams = new URLSearchParams()
    
    if (params.email) queryParams.append('email', params.email)
    if (params.mobile_no) {
      // Clean phone number (remove non-numeric characters)
      const cleanedMobile = params.mobile_no.replace(/\D/g, '')
      queryParams.append('mobile_no', cleanedMobile)
    }
    if (params.first_name) queryParams.append('first_name', params.first_name)
    if (params.last_name) queryParams.append('last_name', params.last_name)
    if (params.birthdate) queryParams.append('birthdate', params.birthdate)
    if (params.security_number) queryParams.append('security_number', params.security_number)
    
    const response = await apiClient.get<InviteDetails[]>(
      `/look_up?${queryParams.toString()}`,
      {
        skipAuth: true,
      } as ExtendedAxiosRequestConfig
    )
    
    return Array.isArray(response.data) ? response.data : []
  } catch (error) {
    // Return empty array on error - lookup is optional
    console.error('Invite lookup failed:', error)
    return []
  }
}

/**
 * Get captcha configuration
 * Returns captcha site key if captcha is enabled
 * 
 * @returns Captcha site key or null
 */
export async function getCaptchaConfig(): Promise<string | null> {
  try {
    const response = await apiClient.get<{ captcha_details?: string }>(
      '/roles',
      {
        skipAuth: true,
      } as ExtendedAxiosRequestConfig
    )
    
    return response.data?.captcha_details || null
  } catch (error) {
    console.error('Failed to get captcha config:', error)
    return null
  }
}
